import * as i0 from '@angular/core';
import { InjectionToken, Directive, Optional, Inject, Input, HostListener, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';

const AUTO_SIZE_INPUT_OPTIONS = new InjectionToken('autosize-input-options');
const DEFAULT_AUTO_SIZE_INPUT_OPTIONS = {
    extraWidth: 0,
    includeBorders: false,
    includePadding: true,
    includePlaceholder: true,
    maxWidth: -1,
    minWidth: -1,
    setParentWidth: false,
    usePlaceHolderWhenEmpty: false,
};

class AutoSizeInputDirective {
    constructor(element, ngModel, ngControl, options, renderer) {
        this.element = element;
        this.ngModel = ngModel;
        this.ngControl = ngControl;
        this.options = options;
        this.renderer = renderer;
        this.extraWidth = this.defaultOptions.extraWidth;
        this.includeBorders = this.defaultOptions.includeBorders;
        this.includePadding = this.defaultOptions.includePadding;
        this.includePlaceholder = this.defaultOptions.includePlaceholder;
        this.maxWidth = this.defaultOptions.maxWidth;
        this.minWidth = this.defaultOptions.minWidth;
        this.setParentWidth = this.defaultOptions.setParentWidth;
        this.usePlaceHolderWhenEmpty = this.defaultOptions.usePlaceHolderWhenEmpty;
        this.useValueProperty = false;
        this.destroy$ = new Subject();
    }
    get borderWidth() {
        return this.includeBorders ? this.sumPropertyValues(['border-right-width', 'border-left-width']) : 0;
    }
    get defaultOptions() {
        return this.options || DEFAULT_AUTO_SIZE_INPUT_OPTIONS;
    }
    get paddingWidth() {
        return this.includePadding ? this.sumPropertyValues(['padding-left', 'padding-right']) : 0;
    }
    get style() {
        return getComputedStyle(this.element.nativeElement, '');
    }
    ngAfterViewInit() {
        if (this.ngModel) {
            this.ngModel.valueChanges?.pipe(tap(() => this.updateWidth()), takeUntil(this.destroy$)).subscribe();
        }
        else if (this.ngControl) {
            this.ngControl.valueChanges?.pipe(tap(() => this.updateWidth()), takeUntil(this.destroy$)).subscribe();
            this.updateWidth();
        }
        else {
            this.updateWidth();
        }
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    onInput(event) {
        if (!this.ngModel && !this.ngControl) {
            this.updateWidth();
        }
    }
    setWidth(width) {
        const { nativeElement } = this.element;
        const parent = this.renderer.parentNode(nativeElement);
        this.setParentWidth ? this.renderer.setStyle(parent, 'width', width + 'px')
            : this.renderer.setStyle(nativeElement, 'width', width + 'px');
    }
    setWidthUsingText(text) {
        this.setWidth(this.textWidth(text) + this.extraWidth + this.borderWidth + this.paddingWidth);
    }
    // Check placeholder width settings and set text width
    textForWidth(inputText, placeHolderText, setPlaceHolderWidth) {
        return (setPlaceHolderWidth && (inputText.length === 0 || !this.usePlaceHolderWhenEmpty)) ?
            placeHolderText : inputText;
    }
    textWidth(value) {
        const ctx = this.renderer.createElement('canvas').getContext('2d');
        const { fontStyle, fontVariant, fontWeight, fontSize, fontFamily, letterSpacing } = this.style;
        // font string format: {normal, normal, 700, 20px, Roboto, "Helvetica Neue", sans-serif}
        ctx.font = fontStyle + ' ' + fontVariant + ' ' + fontWeight + ' ' + fontSize + ' ' + fontFamily;
        ctx.letterSpacing = letterSpacing;
        return ctx.measureText(value).width;
    }
    updateWidth() {
        const inputText = this.getInputValue();
        const placeHolderText = this.getProperty('placeholder');
        const inputTextWidth = this.textWidth(inputText) + this.extraWidth + this.borderWidth + this.paddingWidth;
        const setMinWidth = this.minWidth > 0 && this.minWidth > inputTextWidth;
        const setPlaceHolderWidth = this.includePlaceholder && placeHolderText.length > 0 &&
            (this.textWidth(placeHolderText) > this.textWidth(inputText));
        const setMaxWidth = this.maxWidth > 0 && (this.maxWidth < inputTextWidth);
        if (setMinWidth) {
            this.setWidth(this.minWidth);
        }
        else if (setMaxWidth) {
            this.setWidth(this.maxWidth);
        }
        else {
            this.setWidthUsingText(this.textForWidth(inputText, placeHolderText, setPlaceHolderWidth));
        }
    }
    getInputValue() {
        let value = '';
        if (this.useValueProperty) {
            value = this.getProperty('value');
        }
        else if (this.ngModel) {
            value = this.ngModel.value;
        }
        else if (this.ngControl) {
            value = this.ngControl.value;
        }
        return value || this.getProperty('value') || '';
    }
    getProperty(property) {
        return this.element.nativeElement?.[property];
    }
    sumPropertyValues(properties) {
        return properties.map(property => parseInt(this.style.getPropertyValue(property), 10))
            .reduce((a, b) => a + b, 0);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AutoSizeInputDirective, deps: [{ token: i0.ElementRef }, { token: i1.NgModel, optional: true }, { token: i1.NgControl, optional: true }, { token: AUTO_SIZE_INPUT_OPTIONS, optional: true }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.3", type: AutoSizeInputDirective, selector: "[autoSizeInput]", inputs: { extraWidth: "extraWidth", includeBorders: "includeBorders", includePadding: "includePadding", includePlaceholder: "includePlaceholder", maxWidth: "maxWidth", minWidth: "minWidth", setParentWidth: "setParentWidth", usePlaceHolderWhenEmpty: "usePlaceHolderWhenEmpty", useValueProperty: "useValueProperty" }, host: { listeners: { "input": "onInput($event.target)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AutoSizeInputDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[autoSizeInput]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1.NgModel, decorators: [{
                    type: Optional
                }] }, { type: i1.NgControl, decorators: [{
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [AUTO_SIZE_INPUT_OPTIONS]
                }] }, { type: i0.Renderer2 }]; }, propDecorators: { extraWidth: [{
                type: Input
            }], includeBorders: [{
                type: Input
            }], includePadding: [{
                type: Input
            }], includePlaceholder: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], setParentWidth: [{
                type: Input
            }], usePlaceHolderWhenEmpty: [{
                type: Input
            }], useValueProperty: [{
                type: Input
            }], onInput: [{
                type: HostListener,
                args: ['input', ['$event.target']]
            }] } });

class AutoSizeInputModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AutoSizeInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.3", ngImport: i0, type: AutoSizeInputModule, declarations: [AutoSizeInputDirective], imports: [CommonModule], exports: [AutoSizeInputDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AutoSizeInputModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AutoSizeInputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        AutoSizeInputDirective,
                    ],
                    exports: [
                        AutoSizeInputDirective,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AUTO_SIZE_INPUT_OPTIONS, AutoSizeInputDirective, AutoSizeInputModule, DEFAULT_AUTO_SIZE_INPUT_OPTIONS };
